import type { CapabilityReport, CodecConfigRecord, StructuredError } from './types';

type WebCodecsCtor = {
  isConfigSupported(config: Record<string, unknown>): Promise<{ supported: boolean; config: Record<string, unknown> }>;
};

function webCodecsCtor(name: 'VideoEncoder' | 'AudioEncoder'): WebCodecsCtor | null {
  const candidate = (globalThis as Record<string, unknown>)[name];
  if (typeof candidate !== 'function') {
    return null;
  }
  const probe = candidate as Partial<WebCodecsCtor>;
  return typeof probe.isConfigSupported === 'function' ? probe as WebCodecsCtor : null;
}

function structuredError(code: string, stage: string, error: unknown): StructuredError {
  return {
    code,
    stage,
    recoverable: true,
    message: error instanceof Error ? error.message : String(error)
  };
}

export async function probeCodec(
  kind: 'VideoEncoder' | 'AudioEncoder',
  config: Record<string, unknown>,
): Promise<CodecConfigRecord> {
  const encoder = webCodecsCtor(kind);
  if (!encoder) {
    return {
      requested: config,
      supported: false,
      effective: null,
      error: {
        code: `${kind.toUpperCase()}_UNAVAILABLE`,
        stage: 'capability-probe',
        recoverable: true,
        message: `${kind} is unavailable in this browser.`
      }
    };
  }

  try {
    const result = await encoder.isConfigSupported(config);
    return {
      requested: config,
      supported: result.supported,
      effective: result.supported ? result.config : null,
      error: null
    };
  } catch (error) {
    return { requested: config, supported: false, effective: null, error: structuredError('CODEC_PROBE_FAILED', 'capability-probe', error) };
  }
}

export function collectCapabilities(): CapabilityReport {
  const canShare = typeof navigator.canShare === 'function'
    ? navigator.canShare({ files: [new File(['probe'], 'probe.txt', { type: 'text/plain' })] })
    : null;
  return {
    secureContext: window.isSecureContext,
    serviceWorker: 'serviceWorker' in navigator,
    videoEncoder: webCodecsCtor('VideoEncoder') !== null,
    audioEncoder: webCodecsCtor('AudioEncoder') !== null,
    opfs: Boolean(navigator.storage && 'getDirectory' in navigator.storage),
    webLocks: 'locks' in navigator,
    canShareFiles: canShare
  };
}
